import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { trapTabKey } from '../../../utils/focus-trap.util';
import { prefersReducedMotion } from '../../../utils/stat-motion';
import { placeGuideCallout, type GuideBox } from './page-guide-layout';
import type { PageGuideSession, PageGuideStep } from './page-guide.model';
import { PageGuideService } from './page-guide.service';
import { revealKindForGuideTarget } from './page-guide-reveal';
import { findGuideTarget, scrollGuideTargetIntoView } from './page-guide-target';

@Component({
  selector: 'app-page-guide',
  templateUrl: './page-guide.component.html',
  styleUrls: ['./page-guide.component.scss'],
})
export class PageGuideComponent implements OnInit, OnDestroy {
  @ViewChild('card') private card?: ElementRef<HTMLElement>;
  @ViewChild('nextBtn') private nextBtn?: ElementRef<HTMLButtonElement>;

  session: PageGuideSession | null = null;
  placed = false;
  reducedMotion = false;
  viewportW = 0;
  viewportH = 0;
  spot: GuideBox = { top: 0, left: 0, width: 0, height: 0 };
  callout: GuideBox = { top: 0, left: 0, width: 0, height: 0 };
  dimTop: GuideBox = { top: 0, left: 0, width: 0, height: 0 };
  dimLeft: GuideBox = { top: 0, left: 0, width: 0, height: 0 };
  dimRight: GuideBox = { top: 0, left: 0, width: 0, height: 0 };
  dimBottom: GuideBox = { top: 0, left: 0, width: 0, height: 0 };

  private sub: Subscription | null = null;
  private layingOut = false;
  private pendingLayout = false;
  private resizeObserver: ResizeObserver | null = null;
  private observed: HTMLElement | null = null;
  private focusedIndex = -1;
  private revealAttemptedIndex = -1;
  private revealQueue: Promise<void> = Promise.resolve();

  constructor(
    private readonly guide: PageGuideService,
    private readonly host: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.reducedMotion = prefersReducedMotion();
    this.sub = this.guide.session$.subscribe((session) => {
      this.session = session;
      this.placed = false;
      if (!session) {
        this.focusedIndex = -1;
        this.revealAttemptedIndex = -1;
        this.disconnectObserver();
        this.cdr.detectChanges();
        return;
      }
      this.cdr.detectChanges();
      this.layout(true);
    });
    window.addEventListener('resize', this.onViewport);
    document.addEventListener('scroll', this.onViewport, true);
    window.visualViewport?.addEventListener('resize', this.onViewport);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.disconnectObserver();
    window.removeEventListener('resize', this.onViewport);
    document.removeEventListener('scroll', this.onViewport, true);
    window.visualViewport?.removeEventListener('resize', this.onViewport);
  }

  get step(): PageGuideStep | null {
    if (!this.session) return null;
    return this.session.steps[this.session.index] ?? null;
  }

  get stepLabel(): string {
    if (!this.session) return '';
    return `${this.session.index + 1} / ${this.session.steps.length}`;
  }

  get isFirst(): boolean {
    return !this.session || this.session.index <= 0;
  }

  get isLast(): boolean {
    return !!this.session && this.session.index >= this.session.steps.length - 1;
  }

  close(): void {
    this.guide.close();
  }

  back(): void {
    this.guide.back();
  }

  next(): void {
    this.guide.next();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.session) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const card = this.cardElement();
    if (card) trapTabKey(event, card);
  }

  private readonly onViewport = (): void => {
    if (!this.session) return;
    this.layout(false);
  };

  private layout(allowSkip: boolean): void {
    const session = this.session;
    if (!session) return;
    if (this.layingOut) {
      this.pendingLayout = true;
      return;
    }
    const step = this.step;
    if (!step) return;
    const kind = revealKindForGuideTarget(step.target);
    if (allowSkip && kind && this.revealAttemptedIndex !== session.index) {
      this.revealAttemptedIndex = session.index;
      const index = session.index;
      this.revealQueue = this.revealQueue
        .catch(() => undefined)
        .then(async () => {
          if (!this.session || this.session.index !== index) return;
          try {
            await this.guide.reveal(step.target);
          } catch {
            /* The step still lays out or skips. */
          }
          this.afterReveal(index);
        });
      return;
    }
    const target = findGuideTarget(step.target);
    if (!target) {
      if (allowSkip) queueMicrotask(() => this.guide.next());
      return;
    }
    this.layingOut = true;
    try {
      scrollGuideTargetIntoView(target);
      this.observe(target);
      const card = this.cardElement();
      const rect = target.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const calloutW = card?.offsetWidth || Math.min(320, vw - 32);
      const calloutH = card?.offsetHeight || 168;
      const placement = placeGuideCallout(
        { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom },
        { width: vw, height: vh },
        { width: calloutW, height: calloutH },
      );
      const unchanged =
        this.placed &&
        sameBox(this.spot, placement.spot) &&
        sameBox(this.callout, placement.callout) &&
        this.viewportW === vw &&
        this.viewportH === vh;
      if (unchanged) return;
      this.viewportW = vw;
      this.viewportH = vh;
      this.spot = placement.spot;
      this.callout = placement.callout;
      this.dimTop = { top: 0, left: 0, width: vw, height: Math.max(0, placement.spot.top) };
      this.dimLeft = {
        top: placement.spot.top,
        left: 0,
        width: Math.max(0, placement.spot.left),
        height: placement.spot.height,
      };
      this.dimRight = {
        top: placement.spot.top,
        left: placement.spot.left + placement.spot.width,
        width: Math.max(0, vw - (placement.spot.left + placement.spot.width)),
        height: placement.spot.height,
      };
      this.dimBottom = {
        top: placement.spot.top + placement.spot.height,
        left: 0,
        width: vw,
        height: Math.max(0, vh - (placement.spot.top + placement.spot.height)),
      };
      this.placed = true;
      this.cdr.detectChanges();
      if (this.session && this.focusedIndex !== this.session.index) {
        this.focusedIndex = this.session.index;
        this.nextBtn?.nativeElement.focus();
      }
    } finally {
      this.layingOut = false;
      if (this.pendingLayout && this.session) {
        this.pendingLayout = false;
        queueMicrotask(() => this.layout(true));
      }
    }
  }

  private afterReveal(index: number): void {
    if (!this.session || this.session.index !== index) return;
    this.layout(true);
  }

  private cardElement(): HTMLElement | null {
    return this.card?.nativeElement ?? this.host.nativeElement.querySelector('.tt-page-guide__card');
  }

  private observe(target: HTMLElement): void {
    if (this.observed === target) return;
    this.disconnectObserver();
    if (typeof ResizeObserver === 'undefined') return;
    this.observed = target;
    this.resizeObserver = new ResizeObserver(() => this.layout(false));
    this.resizeObserver.observe(target);
  }

  private disconnectObserver(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.observed = null;
  }
}

function sameBox(a: GuideBox, b: GuideBox): boolean {
  return (
    Math.abs(a.top - b.top) < 1 &&
    Math.abs(a.left - b.left) < 1 &&
    Math.abs(a.width - b.width) < 1 &&
    Math.abs(a.height - b.height) < 1
  );
}
